import React, { useState } from 'react'
import {
  Button,
  TextField,
  SelectField,
  Slider,
  Box,
  Heading,
  Divider,
} from 'components'
import {
  createJobBoardPost,
  decorateJobBoardPost,
  editJobBoardPost,
  getCitiesFromMarkets,
  getIndustriesFromMarkets,
  getRolesFromMarkets,
  slugify,
} from 'utils'
import DateFnsUtils from '@date-io/date-fns'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers'
import { InputLabel } from '@material-ui/core'
import { addMonths, addWeeks, subDays } from 'date-fns'
import JobCard from 'components/freelancer/JobCard'

const JobBoardFormBipoc = ({
  defaultValues = JOB_BOARD_INITIAL_STATE,
  markets = {},
  user,
  history,
}) => {
  console.log('JobBoardFormBipoc initialized with:', { markets, user, history })

  const [formState, _setFormState] = useState({
    email: user?.email || '',
    ...defaultValues,
  })
  const setFormState = (key) => (v) =>
    _setFormState((formState) => ({ ...formState, [key]: v }))

  const [step, setStep] = useState(0)
  const [isPreview, setIsPreview] = useState(false)
  const cities = getCitiesFromMarkets(markets)
  const roles = getRolesFromMarkets(markets)

  const onPrev = () => setStep((s) => s - 1)

  // Generate a title if one doesn't exist
  const generateTitle = () => {
    if (formState.title) return formState.title

    const role = roles.find(r => r.roleId === formState.roleId)?.label || ''
    const projectType = formState.projectType ? PROJECT_TYPE_CHOICES.find(
      t => slugify(t) === formState.projectType
    ) : ''

    if (role && projectType) {
      return `${role} for ${projectType}`
    } else if (role) {
      return role
    }
    return ''
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    if (step < 1) {
      setStep((s) => s + 1)
    } else if (formState.id) {
      if (!isPreview) {
        // Generate title if needed
        const updatedFormState = {
          ...formState,
          title: generateTitle()
        }
        setFormState('title')(updatedFormState.title)
        console.log('Setting preview for edit with data:', updatedFormState)
        return setIsPreview(true)
      }

      console.log('Submitting edit job post with data:', formState);
      console.log('User:', user);

      try {
        await editJobBoardPost(formState, user)
        console.log('Job post edited successfully')
        history.push('/job_board/list')
      } catch (error) {
        console.error('Error editing job post:', error)
      }
    } else {
      if (!isPreview) {
        // Generate title if needed
        const updatedFormState = {
          ...formState,
          title: generateTitle()
        }
        setFormState('title')(updatedFormState.title)
        console.log('Setting preview for new post with data:', updatedFormState)
        return setIsPreview(true)
      }

      console.log('Submitting new job post with data:', formState);
      console.log('User:', user);

      try {
        await createJobBoardPost(formState, user)
        console.log('Job post created successfully')
        history.push('/job_board/list')
      } catch (error) {
        console.error('Error creating job post:', error)
      }
    }
  }
  const maxDate = addMonths(new Date(), 6)

  if (isPreview) {
    const cities = getCitiesFromMarkets(markets)
    const roles = getRolesFromMarkets(markets)
    const industries = getIndustriesFromMarkets(markets)
    console.log('Preview data:', { formState, cities, roles, industries })
    const post = decorateJobBoardPost(formState, cities, roles, industries)
    console.log('Decorated post for preview:', post)

    return (
      <Box>
        <Heading>Check your post</Heading>
        <Box width={250} className="box-wrap" display="flex" mx="auto">
          <JobCard jobPost={post} />
        </Box>
        <Box width={280} display="flex" mt={4} mx="auto">
          <Button
            variant="outlined"
            color="gray"
            onClick={() => setIsPreview(false)}
            style={{ flex: 1, marginRight: 16 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              console.log('Confirm button clicked, submitting data:', formState);
              onSubmit(e);
            }}
            style={{ flex: 1 }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Heading>Create New Job Post</Heading>

      <form onSubmit={onSubmit}>
        {step === 0 && (
          <>
            <Box display="flex" flex={1}>
              <SelectField
                required
                flex={1}
                value={formState.roleId}
                name="role"
                label="Role"
                onChange={(e) => setFormState('roleId')(e.target.value)}
                choices={roles.map(({ roleId, label }) => ({
                  label,
                  value: roleId,
                }))}
              />
              <Box mx={1} />
              <SelectField
                required
                flex={1}
                name="projectType"
                label="Type of Project"
                value={formState.projectType}
                onChange={(e) => setFormState('projectType')(e.target.value)}
                choices={PROJECT_TYPE_CHOICES.map((string) => ({
                  label: string,
                  value: slugify(string),
                }))}
              />
            </Box>

            <Box>
              <InputLabel shrink>Years of Experience *</InputLabel>
              <Box display="flex" alignItems="center">
                <TextField
                  value={`${formState.experience[0]} - ${formState.experience[1]}`}
                  style={{ width: 80, marginRight: 15 }}
                />

                <Slider
                  value={formState.experience}
                  min={1}
                  max={20}
                  onChange={(e, value) => setFormState('experience')(value)}
                />
              </Box>
            </Box>

            <TextField
              required
              multiline
              rows={3}
              value={formState.description}
              onChange={(e) => setFormState('description')(e.target.value)}
              name="description"
              label="Description"
              placeholder="Include details about the job requirements and responsibilities"
            />

            <TextField
              required
              value={formState.company}
              onChange={(e) => setFormState('company')(e.target.value)}
              name="company"
              label="Company"
            />

            <TextField
              required
              value={formState.email}
              onChange={(e) => setFormState('email')(e.target.value)}
              name="email"
              label="Contact email or application link"
              placeholder="example@company.com or https://apply.company.com"
            />
          </>
        )}

        {step === 1 && (
          <>
            <Box display="flex" flex={1}>
              <SelectField
                required
                flex={1}
                name="hiringArea"
                label="Hiring Area"
                value={formState.hiringArea}
                onChange={(e) => setFormState('hiringArea')(e.target.value)}
                choices={HIRING_AREA_CHOICES.map((string) => ({
                  label: string,
                  value: slugify(string),
                }))}
              />
              <Box mx={1} />
              <SelectField
                flex={1}
                required={formState.hiringArea === 'local-only'}
                name="cityId"
                label="City"
                value={formState.cityId}
                onChange={(e) => setFormState('cityId')(e.target.value)}
                choices={cities.map(({ id, label }) => ({
                  label,
                  value: id,
                }))}
              />
            </Box>

            <Box display="flex" flex={1}>
              <SelectField
                flex={1}
                required
                name="commitment"
                label="How many days is this job?"
                value={formState.commitment}
                onChange={(e) => setFormState('commitment')(e.target.value)}
                choices={JOB_DURATION_CHOICES.map((string) => ({
                  label: string,
                  value: slugify(string),
                }))}
              />
              <Box mx={1} />
              <SelectField
                flex={1}
                required
                name="unionStatus"
                label="Is it Union or Non-Union"
                value={formState.unionStatus}
                onChange={(e) => setFormState('unionStatus')(e.target.value)}
                choices={UNION_STATUS_CHOICES.map((string) => ({
                  label: string,
                  value: slugify(string),
                }))}
              />
            </Box>

            <Box display="flex" flex={1} alignItems="flex-end">
              <TextField
                required
                value={formState.payAmount}
                onChange={(e) => setFormState('payAmount')(e.target.value)}
                placeholder="Mandatory"
                name="pay"
                label="Pay"
                wrapperProps={{ flex: 1 }}
              // InputProps={{
              //   startAdornment: '$',
              // }}
              />
              <Box mx={1} />
              <SelectField
                flex={1}
                name="payRate"
                label="Billing Style"
                value={formState.payRate}
                onChange={(e) => setFormState('payRate')(e.target.value)}
                choices={BILLING_STYLE_CHOICES.map((string) => ({
                  label: string,
                  value: slugify(string),
                }))}
              />
            </Box>

            <Box display="flex" flex={1}>
              <KeyboardDatePicker
                required
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="start_date"
                label="Start Date*"
                minDate={subDays(new Date(), 7)}
                maxDate={subDays(maxDate, 7)}
                value={formState.startDate}
                onChange={(v) => {
                  setFormState('startDate')(v)
                  if (v > formState.endDate) {
                    setFormState('endDate')(v)
                  }
                }}
              />
              <Box mx={1} />

              <KeyboardDatePicker
                required
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="end_date"
                label="Posting Close Date"
                minDate={new Date()}
                maxDate={maxDate}
                value={formState.endDate}
                onChange={(v) => {
                  setFormState('endDate')(v)
                  if (v < formState.startDate) {
                    setFormState('startDate')(v)
                  }
                }}
              />
            </Box>
          </>
        )}

        <Box display="flex" mt={4}>
          {step > 0 && (
            <Button
              variant="contained"
              color="gray"
              onClick={onPrev}
              style={{ flex: 1, marginRight: 16 }}
            >
              Prev
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            type="submit"
            style={{ flex: 1 }}
          >
            {step < 1 ? 'Next' : 'Preview Job Post'}
          </Button>
        </Box>
      </form>
    </MuiPickersUtilsProvider>
  )
}

export default JobBoardFormBipoc

const PROJECT_TYPE_CHOICES = [
  'TV Series',
  'Documentary',
  'Feature',
  'Short Film',
  'Reality TV',
  'Music Video',
  'AR',
  'VR',
  'Digital',
  'Interactive Commercial',
  'Other'
]

const HIRING_AREA_CHOICES = ['Local Only', 'Anyone can apply']

const JOB_DURATION_CHOICES = ['3 days', 'Full time', 'Part time']

const UNION_STATUS_CHOICES = ['Union', 'Non-Union']

const BILLING_STYLE_CHOICES = ['per Week', 'per Month']

const JOB_BOARD_INITIAL_STATE = {
  title: '',
  description: '',
  roleId: '',
  cityId: '',
  company: '',
  industryId: '', // Not used in the form but expected by backend
  workingStyle: 'remote', // Not used in form but expected by backend
  payAmount: '',
  commitment: '', // Maps to jobDuration
  payRate: '', // Maps to billingStyle
  projectType: '',
  hiringArea: 'local-only',
  unionStatus: '',
  experience: [1, 20],
  startDate: new Date(),
  endDate: addWeeks(new Date(), 1),
  skills: [], // Not used in form but expected by backend
}
